import React, { useState, useEffect, useRef } from 'react';
import { Box, Image, Skeleton } from '@chakra-ui/react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';

const RotaryLogo = ({ containerWidth }) => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const supabase = useSupabaseClient();
  const containerRef = useRef(null);

  useEffect(() => {
    async function fetchLogo() {
      try {
        setLoading(true);
        setError(null);

        // First, list all files in the club-logos folder
        const { data: files, error: listError } = await supabase
          .storage
          .from('logos')
          .list('club-logos', {
            limit: 100,
            offset: 0,
            sortBy: { column: 'created_at', order: 'desc' }
          });

        if (listError) {
          throw listError;
        }

        if (!files || files.length === 0) {
          throw new Error('No logo files found');
        }

        // Get the most recent file
        const mostRecentLogo = files[0];

        // Get the public URL for the logo
        const { data: publicUrl } = supabase
          .storage
          .from('logos')
          .getPublicUrl(`club-logos/${mostRecentLogo.name}`);

        if (!publicUrl?.publicUrl) {
          throw new Error('Could not get public URL for logo');
        }

        setLogoUrl(publicUrl.publicUrl);
      } catch (err) {
        console.error('Error fetching logo:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchLogo();
  }, [supabase]);

  // Calculate dimensions to maintain aspect ratio
  const calculateDimensions = (imgWidth, imgHeight, maxWidth) => {
    const padding = 20; // 10px padding on each side
    const availableWidth = maxWidth - padding;
    const aspectRatio = imgWidth / imgHeight;
    
    const width = Math.min(availableWidth, imgWidth);
    const height = width / aspectRatio;
    
    return { width, height };
  };

  if (loading) {
    return (
      <Skeleton
        width={containerWidth ? `${containerWidth - 20}px` : "260px"}
        height="60px"
        startColor="gray.100"
        endColor="gray.300"
      />
    );
  }

  if (error) {
    return (
      <Box
        width={containerWidth ? `${containerWidth - 20}px` : "260px"}
        height="60px"
        bg="gray.100"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="gray.500"
        fontSize="sm"
      >
        Unable to load logo
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      width={containerWidth ? `${containerWidth - 20}px` : "260px"}
      height="auto"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Image
        src={logoUrl}
        alt="Rotary Logo"
        maxWidth="100%"
        height="auto"
        objectFit="contain"
        onLoad={(e) => {
          const img = e.target;
          const { width, height } = calculateDimensions(
            img.naturalWidth,
            img.naturalHeight,
            containerWidth || 280
          );
          img.style.width = `${width}px`;
          img.style.height = `${height}px`;
        }}
      />
    </Box>
  );
};

export default RotaryLogo;
